/* eslint-disable */
export const DEFAULT_CVV_LENGTH = 3;

/**
 * Used for internal mapping of card brands to types.
 * @type {Object}
 */
const paymentCardTypes = {
    amex: 'amex',
    visa: 'visa',
    cup: 'cup',
    mastercard: 'mastercard',
    discover: 'discover',
    maestro: 'maestro',
    dinersClub: 'dinersClub',
    JCB: 'JCB',
    astroPayCard:'astroPayCard',
};

const paymentCardRules = {
    [paymentCardTypes.amex]: {
        lengths: [15],
        prefix: /^3[47]/,
        cvv: 4,
    },
    [paymentCardTypes.cup]: {
        lengths: [16, 17, 18, 19],
        prefix: /^(5610|56022[1-5]|62)/,
        cvv: DEFAULT_CVV_LENGTH,
    },
    [paymentCardTypes.dinersClub]: {
        lengths: [14],
        prefix: /^(30(95|[0-5])|3[689])/,
        cvv: DEFAULT_CVV_LENGTH,
    },
    [paymentCardTypes.discover]: {
        lengths: [16, 17, 18, 19],
        prefix: /^(6011|65|64)/,
        cvv: DEFAULT_CVV_LENGTH,
    },
    [paymentCardTypes.JCB]: {
        lengths: [16],
        prefix: /^(35(2[89]|[3-8][0-9]))/,
        cvv: DEFAULT_CVV_LENGTH,
    },
    [paymentCardTypes.maestro]: {
        lengths: [16, 17, 18, 19],
        prefix: /^(5018|5020|5038|5893|6304|6759|676[1-3]|0604)/,
        cvv: DEFAULT_CVV_LENGTH,
    },
    [paymentCardTypes.mastercard]: {
        lengths: [16],
        prefix: /^5[1-5]/,
        cvv: DEFAULT_CVV_LENGTH,
    },
    [paymentCardTypes.visa]: {
        lengths: [16, 19],
        prefix: /^4/,
        cvv: DEFAULT_CVV_LENGTH,
    },
    [paymentCardTypes.astroPayCard]: {
        lengths: [16],
        prefix: /^1\d[12]/,
        cvv: 4,
    },
};

/**
 * Map API payment card brands to internal values.
 * @type {Object}
 */
const paymentCardBrandToTypeMap = {
    Visa: paymentCardTypes.visa,
    ChinaUnionPay: paymentCardTypes.cup,
    MasterCard: paymentCardTypes.mastercard,
    'American Express': paymentCardTypes.amex,
    Discover: paymentCardTypes.discover,
    Maestro: paymentCardTypes.maestro,
    JCB: paymentCardTypes.JCB,
    'Diners Club': paymentCardTypes.dinersClub,
    'AstroPay Card': paymentCardTypes.astroPayCard,
};

/**
 * Define masks for different lengths of PAN. Use `true` to include any numbers
 * after the previous groups into a single large group.
 * @type {Object}
 */
const masks = {
    14: [5, 4, 5, true],
    15: [4, 6, 5, true],
    16: [4, 4, 4, 4, true],
    17: [4, 4, 4, 4, true],
    18: [4, 4, 4, 4, true],
    19: [4, 4, 4, 4, true],
};

/**
 * Validates the Luhn calculation for the provided PAN.
 * @param pan {string}
 * @returns {boolean}
 */
function validLuhn(pan) {
    const matrix = [
        [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
        [0, 2, 4, 6, 8, 1, 3, 5, 7, 9],
    ];
    let panLength = pan.length;
    let multiplier = 0;
    let sum = 0;

    while (panLength--) {
        sum += matrix[multiplier][parseInt(pan.charAt(panLength), 10)];
        multiplier ^= 1;
    }

    return sum % 10 === 0 && sum > 0;
}

/**
 * Strip spaces and dashes from a PAN. If provided also strips the custom delimiter.
 * @param pan {string|number}
 * @returns {string}
 */
function stripPAN(pan, delimiter) {
    let strippedPan = String(pan).replace(/\s+|-/g, '');
    if (strippedPan.includes(delimiter)) {
        const pattern = new RegExp(delimiter, 'g');
        return strippedPan.replace(pattern, '');
    }
    return strippedPan;
}

function getMatchingCardBrand(pan) {
    const brands = Object.keys(paymentCardRules);
    let index = 0;
    const length = brands.length;
    for (; index < length; index++) {
        const brand = brands[index];
        if (paymentCardRules[brand].prefix.test(pan)) {
            return brand;
        }
    }
    return null;
}

function getGroupedPAN(pan, brand) {
    const panLength = pan.length;
    const brandDefinition = paymentCardRules[brand];
    const mask = (() => {
        const index = brandDefinition.lengths.indexOf(panLength);
        //if there's a perfect match use it
        if (index > -1) {
            return masks[String(panLength)];
        }
        //otherwise look for the known length closest to the current PAN length
        let closestLength = brandDefinition.lengths.reduce((len, value) => {
            if (panLength <= value && len === 0) {
                len = value;
            }
            return len;
        }, 0);
        //if we don't have a match use the longest length for the card brand
        if (!closestLength) {
            [closestLength] = [...brandDefinition.lengths].reverse();
        }
        return masks[String(closestLength)];
    })();
    return applyMask(mask, pan);
}

function applyMask(mask, pan) {
    let masked = [];
    let index = 0;
    const panLength = pan.length;
    mask.forEach(increment => {
        if (index < panLength) {
            const end = increment === true ? panLength : increment;
            const value = pan.substring(index, index + end);
            index = index + increment;
            masked.push(value);
        }
    });
    return masked;
}

function padPAN(bin, lastFour, brand) {
    const [length] = [...paymentCardRules[brand].lengths];
    const currentLength = bin.length + lastFour.length;
    const padAmount = (() => {
        if (currentLength >= length) {
            return 0;
        }
        return length - currentLength;
    })();
    const pad = padAmount ? new Array(padAmount + 1).join('*') : '';
    return `${bin}${pad}${lastFour}`;
}

/**
 * Format a full or partial PAN based on the brand.
 * @param pan {string}
 * @param bin {string}
 * @param lastFour {string}
 * @param brand {string}
 * @param concat {boolean}
 * @param delimiter {string}
 * @returns {*}
 */
export default function formatPAN({pan = null, bin = null, lastFour = null, brand = null, concat = true, delimiter = '  '}) {
    let groups = [];
    let matchedBrand = brand;
    if (pan !== null) {
        let strippedPan = stripPAN(pan, delimiter);
        matchedBrand = getMatchingCardBrand(strippedPan);
        //group the invalid PAN as faithfully as possible
        if (matchedBrand in paymentCardRules) {
            groups = getGroupedPAN(strippedPan, matchedBrand);
        }
        //otherwise return same value provided in the pan
        else {
            groups = [pan];
        }
    }
    else if (bin !== null && lastFour !== null) {
        if (brand && brand in paymentCardBrandToTypeMap) {
            matchedBrand = paymentCardBrandToTypeMap[brand];
        }
        else {
            matchedBrand = paymentCardTypes.visa;
        }
        groups = getGroupedPAN(padPAN(bin, lastFour, matchedBrand), matchedBrand);
    }

    if (concat) {
        return groups.join(delimiter);
    }
    return {groups, type: matchedBrand, valid: validLuhn(groups.join(''))};
}

export function getCvvLength(type) {
    const cardType = paymentCardBrandToTypeMap[type];
    if (cardType) {
        return paymentCardRules[cardType].cvv;
    }
    return DEFAULT_CVV_LENGTH;
}
